import { fileTypes } from '../../Whatsapp/Constant';
import { WaTemplateItem, WaVariableMap } from '../../../Models/DataSources/SmartSendWhatsApp';
import { waVariables } from './waTokens';

// SmartSend part B — turning one approved template + one sample row into the shape the LIVE
// `WhatsappMobilePreview` already speaks. Pure module (no React, no redux, no i18n).
//
// This is a TRANSLATION of an existing preview, not a new design (14-W3 §0 / the live precedent
// `src/style/classes/whatsappStyles.js`). RTL is already solved there (`isRTL ? 'left' : 'right'`)
// and is NOT solved again here.

// 🔴 MEDIA TYPE COMES FROM THE URL EXTENSION, NEVER FROM THE JSON'S `media_type`.
// The binding precedent is `SaveCampain.tsx` @ NewClalDesign ("URL extension is ground truth"),
// and the reason is in Clal's own production data: templates 4533/4534 declare `"image"` over a
// `.pdf` file. `getFileType` (screens/Whatsapp/Common.tsx:82-94) is that rule, already written —
// the server's `MediaType` is used only as the fallback when the URL carries no usable
// extension, which is exactly what `WhatsappMobilePreview.resolveFileType` does with it.
export const mediaFileData = (tpl: WaTemplateItem | null): { fileLink: string; fileType: string } => {
    // 2026-10-08 WA-MEDIA — keyed on the URL, not on HasMedia: a `card` template carries its header
    // image at $.card.media (HasMedia is false for it), and the unit's send now carries that image,
    // so the phone must show it. A text template has MediaUrl null and is unchanged.
    if (!tpl || !tpl.MediaUrl) return { fileLink: '', fileType: '' };
    return { fileLink: tpl.MediaUrl, fileType: (tpl.MediaType ?? '').toLowerCase() };
};

export const isSupportedMedia = (link: string, declared?: string | null): boolean => {
    const lower = (link ?? '').toLowerCase();
    if (lower.indexOf('.pdf') > -1) return true;
    if (/\.(png|jpe?g|gif|webp)(\?|#|$)/.test(lower)) return true;
    if (/\.(mp4|3gp)(\?|#|$)/.test(lower)) return true;
    const d = (declared ?? '').toLowerCase();
    return d === fileTypes.IMAGE || d === fileTypes.VIDEO || d === fileTypes.DOCUMENT;
};

// Fill `{{n}}` from the sample row.
//
// 🔴 EVERY placeholder gets a value, including the unmapped ones — and an unmapped one is filled
// with its own `{{n}}`. `getTemplateTextWithVariable` (screens/Whatsapp/Common.tsx:349-365) looks
// each `{{n}}` up in the values object and substitutes whatever comes back, so a missing key
// prints the literal string "undefined" inside the phone. The operator would read that as a
// value the recipient will actually receive.
//
// The "this one is not mapped" signal belongs in the variable ROW and in the stepper — never
// inside the bubble, which must show only what the recipient would see (14-W3 §1).
export const fillPreviewValues = (
    placeholderCount: number,
    serverValues: { [index: string]: string } | null | undefined,
): { [index: string]: string } => {
    const out: { [index: string]: string } = {};
    waVariables(placeholderCount).forEach((v) => {
        const key = String(v.index);
        const raw = serverValues ? serverValues[key] : undefined;
        out[key] = raw != null && raw !== '' ? raw : v.token;
    });
    return out;
};

// Which variables are still unbound — the red marks live on these rows and in the stepper.
export const unboundIndexes = (placeholderCount: number, map: WaVariableMap): number[] =>
    waVariables(placeholderCount)
        .filter((v) => {
            const b = map[v.index];
            return !(b && ((b.columnId != null && b.columnId > 0) || (b.staticValue != null && b.staticValue !== '')));
        })
        .map((v) => v.index);

// The `templateData` prop of `WhatsappMobilePreview`. Buttons are NOT rendered from our side:
// the MVP maps body variables only, a `{{n}}` in a URL button is filled per row by the sender
// (`GetCallActionButtonClientIdVariable`) and a template whose button variable it cannot fill
// is disabled in the picker (`ButtonVarUnresolved`). An empty quick-reply array is what the
// live component treats as "no buttons".
export const previewTemplateData = (bodyText: string) => ({
    templateText: bodyText ?? '',
    templateButtons: [] as any[],
});
